import type { User as SupabaseUser } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '../types';

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
}

// Export the context so the hook can access it
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef<string | null>(null);

  // 1) On mount, get existing session from cache (no network call), then
  //    listen for subsequent auth changes. Mirrors the pattern that works
  //    reliably on mobile/tablet.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSupabaseUser(session?.user ?? null);
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // 2) Fetch profile whenever supabaseUser changes
  useEffect(() => {
    if (!supabaseUser) {
      currentUserIdRef.current = null;
      setUser(null);
      return;
    }

    // Skip if we already loaded this user's profile
    if (currentUserIdRef.current === supabaseUser.id) return;

    let mounted = true;

    async function loadProfile() {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('full_name, role')
          .eq('id', supabaseUser!.id)
          .single();

        if (!mounted) return;

        if (error) throw error;

        currentUserIdRef.current = supabaseUser!.id;
        setUser({
          id: supabaseUser!.id,
          email: supabaseUser!.email!,
          full_name: data?.full_name ?? null,
          role: data?.role ?? 'viewer',
        });
      } catch (err) {
        console.error('Error fetching user profile:', err);
        if (!mounted) return;
        currentUserIdRef.current = supabaseUser!.id;
        setUser({
          id: supabaseUser!.id,
          email: supabaseUser!.email!,
          full_name: null,
          role: 'viewer',
        });
      }
    }

    loadProfile();
    return () => {
      mounted = false;
    };
  }, [supabaseUser]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          name: fullName,
        },
      },
    });
    if (error) throw error;

    if (data.user) {
      await supabase.from('user_profiles').upsert(
        {
          id: data.user.id,
          full_name: fullName || null,
          role: 'viewer' as const,
        },
        { onConflict: 'id' }
      );
    }
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    currentUserIdRef.current = null;
    setUser(null);
  }, []);

  // Memoize the context value to prevent cascading re-renders
  // of every useAuth() consumer on unrelated parent re-renders.
  const value = useMemo<AuthContextType>(
    () => ({ user, loading, signIn, signUp, signOut }),
    [user, loading, signIn, signUp, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
